import { AttributeTextValue, CartLine, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from './getExtensionPropertyValueByKey';
import { SupersessionKey } from '../themes/gardner-theme/models/SupersessionKey';
import { ICartState } from '@msdyn365-commerce/global-state';
import { IWarehouseMapping, WarehouseInventoryType } from '../themes/gardner-theme/views/components/inventory-breakdown.component';
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import { SupersessionPreview } from '../themes/gardner-theme/views/components/supersession-breakdown.component';

interface CheckAlternateWarehouseFeatureParams {
    extensionProperties: CommerceProperty[] | undefined;
    allowedDealerTypes: string[];
    designatedCustomerGroupsArr: string[];
    authorizedWarehouseAccounts: string[];
    isAlternateWarehouseFeatureEnabled: boolean;
    customerAccNum: string | undefined;
    isSobo: boolean;
    context: any;
}

export interface IUseWarehouseProps {
    cartLine: CartLine;
    cartState: ICartState | undefined;
    warehouse: WarehouseInventoryType | undefined;
    warehouses: WarehouseInventoryType[] | undefined;
    productExtensionProperties: CommerceProperty[] | undefined;
    appConfig: any;
    isAlternateWarehouseSelectionDisabled: boolean;
    stockLabel: string;
}

export interface AdditionalAltWarehouseDetails {
    CustomerGroup: string;
    FreightTermCodeId: string;
    ProductCode: string;
    GdnOverrideWhAutoSelect: boolean;
}

export type ShipmentType = 'FULL_SINGLE' | 'FULL_MULTI' | 'PARTIAL_SINGLE' | 'PARTIAL_MULTI' | 'FULL_BACKORDER';

export interface IShipmentInfo {
    type: ShipmentType;
    inStockQty: number;
    backorderQty: number;
    warehouseId?: string;
}

export interface WarehouseState {
    hasWarehouseChanged: boolean;
    isAlternateWarehouse: boolean;
    isWarehouseOverride: boolean;
    shouldDisplayAutoSourcedMessage: boolean;
    shouldUpdateWarehouse: boolean;
    totalStock: number;
    inStockLabel: string;
    shipmentInfo: IShipmentInfo;
    getWarehouseNameFromMappings: (wId?: string) => string | undefined;
    warehouses?: WarehouseInventoryType[] | undefined;
    warehouse?: WarehouseInventoryType | undefined;
    isReplacementItem: boolean;
    supersededFromItem: string;
}

export const getAdditionalAltWarehouseDetails = (extensionProperties: CommerceProperty[] | undefined) => {
    return getExtensionPropertyValueByKey(
        extensionProperties,
        SupersessionKey.AltWarehouseAdditional,
        CommercePropertyValueEnum.string
    ) as string;
};

export const checkAlternateWarehouseFeatureAccess = async ({
    extensionProperties,
    allowedDealerTypes,
    designatedCustomerGroupsArr,
    authorizedWarehouseAccounts,
    isAlternateWarehouseFeatureEnabled,
    customerAccNum,
    isSobo,
    context
}: CheckAlternateWarehouseFeatureParams): Promise<boolean> => {
    if (!isSobo && !customerAccNum) {
        return false;
    }

    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;

    if (!additionalAltWarehouseDetails) {
        return false;
    }

    const freightTermCodeMet = allowedDealerTypes.includes(additionalAltWarehouseDetails.FreightTermCodeId);
    const customerGroupMet = designatedCustomerGroupsArr.includes(additionalAltWarehouseDetails.CustomerGroup);
    const isAuthorizedCustNum = typeof customerAccNum === 'string' && authorizedWarehouseAccounts.includes(customerAccNum);

    if (freightTermCodeMet && customerGroupMet && isAlternateWarehouseFeatureEnabled) {
        return true;
    }

    if (isAuthorizedCustNum) {
        return true;
    }

    if (isSobo) {
        const customer = await getCustomer(new GetCustomerInput(context.requestContext.apiSettings), context);
        if (authorizedWarehouseAccounts.includes(customer.AccountNumber)) {
            return true;
        }
    }

    return false;
};

/**
 * Converts a comma- or semicolon-separated string into an array of trimmed strings.
 * For example, "a, b; c" becomes ["a", "b", "c"].
 *
 * @param str - The comma- or semicolon-separated string.
 * @returns An array of trimmed string values.
 */
export const convertStringToArray = (str: string) => {
    return str?.split(/[;,]+/).map(ele => ele.trim()) ?? [];
};

export const getIsAlternateWarehouse = ({
    designatedDealerTypesArr,
    productCodeWhitelistArr,
    extensionProperties
}: {
    designatedDealerTypesArr: string[];
    productCodeWhitelistArr: string[];
    extensionProperties: CommerceProperty[] | undefined;
}) => {
    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;
    if (!additionalAltWarehouseDetails) {
        return true;
    }

    if (designatedDealerTypesArr.includes(additionalAltWarehouseDetails.FreightTermCodeId)) {
        if (productCodeWhitelistArr.includes(additionalAltWarehouseDetails.ProductCode)) {
            return false;
        }
    }
    return true;
};

export const shouldEnableSecondaryWarehouses = (extensionProperties: CommerceProperty[] | undefined) => {
    const additionalAltWarehouseDetails = JSON.parse(
        getAdditionalAltWarehouseDetails(extensionProperties)
    ) as AdditionalAltWarehouseDetails;
    if (!additionalAltWarehouseDetails) {
        return false;
    }

    if (!additionalAltWarehouseDetails.GdnOverrideWhAutoSelect) {
        return true;
    }
    return false;
};

const getShipmentInfo = (
    productQuantity: number,
    totalStock: number,
    warehouses: WarehouseInventoryType[],
    enableSecondaryWarehouses: boolean,
    primaryWarehouse: WarehouseInventoryType | undefined
): IShipmentInfo => {
    // Edge: no stock at all -> everything backordered.
    if (totalStock === 0) {
        return { type: 'FULL_BACKORDER', inStockQty: 0, backorderQty: productQuantity };
    }

    // If disable seconday warehouse and the "primary" warehouse is the first
    // warehouse, keep using the primary warehouse only (existing behaviour).
    if (!enableSecondaryWarehouses && primaryWarehouse?.WarehouseId === warehouses[0]?.WarehouseId) {
        if ((primaryWarehouse?.Available ?? 0) >= productQuantity) {
            return {
                type: 'FULL_SINGLE',
                inStockQty: productQuantity,
                warehouseId: primaryWarehouse?.WarehouseId ?? '',
                backorderQty: 0
            };
        } else {
            return {
                type: 'PARTIAL_SINGLE',
                inStockQty: primaryWarehouse?.Available ?? 0,
                backorderQty: Math.max(productQuantity - (primaryWarehouse?.Available ?? 0), 0),
                warehouseId: primaryWarehouse?.WarehouseId ?? ''
            };
        }
    }

    const backorderQty = Math.max(productQuantity - totalStock, 0);

    let remaining = productQuantity;
    const allocation: { warehouseId?: string; qty: number; name?: string }[] = [];

    for (const w of warehouses) {
        const avail = w.Available ?? 0;
        if (avail <= 0) {
            continue;
        }
        const take = Math.min(avail, remaining);
        if (take > 0) {
            allocation.push({ warehouseId: w.WarehouseId, qty: take, name: w.Name });
            remaining -= take;
        }
        if (remaining <= 0) {
            break;
        }
    }

    const usedCount = allocation.length;
    const inStockQty = productQuantity - backorderQty;

    if (remaining <= 0) {
        if (usedCount === 1) {
            return {
                type: 'FULL_SINGLE',
                inStockQty: productQuantity,
                backorderQty: 0,
                warehouseId: allocation[0].warehouseId ?? ''
            };
        }
        return {
            type: 'FULL_MULTI',
            inStockQty: productQuantity,
            backorderQty: 0
        };
    }

    if (usedCount === 1) {
        return {
            type: 'PARTIAL_SINGLE',
            inStockQty: allocation[0].qty,
            backorderQty,
            warehouseId: allocation[0].warehouseId ?? ''
        };
    }

    return {
        type: 'PARTIAL_MULTI',
        inStockQty,
        backorderQty
    };
};

export const computeWarehouseStateForLine = (
    cartLine: CartLine,
    warehouses: WarehouseInventoryType[] | undefined,
    productExtensionProperties: CommerceProperty[] | undefined,
    appConfig: any,
    stockLabel: string,
    isAlternateWarehouseSelectionDisabled: boolean
): WarehouseState => {
    const warehouseMappings: IWarehouseMapping[] = appConfig.alternateWarehouseMappings || [];
    const warehouse =
        warehouses?.find(
            w => w.WarehouseId === (cartLine.AttributeValues?.find(a => a.Name === 'WarehouseId') as AttributeTextValue)?.TextValue
        ) || warehouses?.[0];
    const getWarehouseNameFromMappings = (warehouseId?: string) =>
        warehouseMappings?.find(mapping => mapping.warehouseId === warehouseId)?.warehouseName;

    const designatedDealerTypesArr = convertStringToArray(appConfig.designatedDealerTypes);
    const productCodeWhitelistArr = convertStringToArray(appConfig.productCodeWhitelist);

    const isAlternateWarehouse = getIsAlternateWarehouse({
        designatedDealerTypesArr,
        productCodeWhitelistArr,
        extensionProperties: productExtensionProperties
    });

    const productQuantity = cartLine?.Quantity ?? 0;
    const totalStock = warehouses?.reduce((sum, w) => sum + (w.Available ?? 0), 0) ?? 0;
    const firstWarehouse = warehouses?.[0];
    const enableSecondaryWarehouses = shouldEnableSecondaryWarehouses(productExtensionProperties);

    const shipmentInfo = getShipmentInfo(productQuantity, totalStock, warehouses ?? [], enableSecondaryWarehouses, firstWarehouse);

    const override = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'IsWarehouseOverrideByQuantity',
        CommercePropertyValueEnum.boolean
    ) as boolean;
    const autoSource = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'shouldDisplayAutoSourcedMessage',
        CommercePropertyValueEnum.boolean
    ) as boolean;
    const hasWarehouseChangedProp = getExtensionPropertyValueByKey(
        cartLine.ExtensionProperties,
        'HasWarehouseChanged',
        CommercePropertyValueEnum.boolean
    ) as boolean;

    const isWarehouseOverride = Boolean(override);
    const shouldDisplayAutoSourcedMessage = Boolean(autoSource);
    const hasWarehouseChanged = Boolean(hasWarehouseChangedProp);

    const isPrimaryWh = warehouse?.WarehouseId === firstWarehouse?.WarehouseId;
    const primaryHasInsufficientStock = productQuantity > (firstWarehouse?.Available ?? 0);
    const shouldAutoSourceBecauseSingleSupplier =
        shipmentInfo.type === 'FULL_SINGLE' &&
        Boolean(shipmentInfo.warehouseId) &&
        shipmentInfo.warehouseId !== firstWarehouse?.WarehouseId;

    const shouldUpdateWarehouse =
        enableSecondaryWarehouses && isPrimaryWh && primaryHasInsufficientStock && shouldAutoSourceBecauseSingleSupplier;

    const inStockLabel = !isAlternateWarehouseSelectionDisabled
        ? !enableSecondaryWarehouses
            ? productQuantity > (warehouse?.Available ?? 0)
                ? `${warehouse?.Available ?? 0} ${stockLabel}`
                : stockLabel
            : productQuantity > totalStock
            ? `${totalStock} ${stockLabel}`
            : stockLabel
        : stockLabel;

    const autoReplacementAttr: AttributeTextValue | undefined = cartLine.AttributeValues?.find(a => a.Name === 'SupersessionIsReplacement');
    const isReplacementItem = autoReplacementAttr?.TextValue === 'true' ? true : false;

    const supersededFromAttr: AttributeTextValue | undefined = cartLine.AttributeValues?.find(a => a.Name === 'SupersessionSourceItemId');
    const supersededFromItem = supersededFromAttr?.TextValue || '';

    return {
        hasWarehouseChanged,
        isAlternateWarehouse,
        isWarehouseOverride,
        shouldDisplayAutoSourcedMessage,
        shouldUpdateWarehouse,
        totalStock,
        inStockLabel,
        shipmentInfo,
        getWarehouseNameFromMappings,
        warehouses,
        warehouse,
        isReplacementItem,
        supersededFromItem
    };
};

export const buildUpdatedCartLine = (cartLine: CartLine, warehouseId: string): CartLine => {
    let updatedExtensionProperties = upsertBoolExtensionProperty(cartLine?.ExtensionProperties, 'HasWarehouseChanged', false);
    updatedExtensionProperties = upsertBoolExtensionProperty(updatedExtensionProperties, 'IsWarehouseOverrideByQuantity', true);

    const updatedAttributeValues = upsertAttributeTextValue(cartLine?.AttributeValues, 'WarehouseId', warehouseId);

    return {
        ...cartLine,
        WarehouseId: warehouseId,
        AttributeValues: updatedAttributeValues,
        ExtensionProperties: updatedExtensionProperties
    };
};

export const upsertAttributeTextValue = (
    attrs: AttributeTextValue[] | undefined,
    name: string,
    textValue: string
): AttributeTextValue[] => {
    const existing = Array.isArray(attrs) ? attrs : [];
    let found = false;

    const next = existing.map(a => {
        if (a?.Name === name) {
            found = true;
            return {
                ...a,
                TextValue: textValue,
                '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
            };
        }
        return a;
    });

    if (!found) {
        next.push({
            Name: name,
            TextValue: textValue,
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue'
        } as AttributeTextValue);
    }

    return next;
};

export const upsertBoolExtensionProperty = (props: CommerceProperty[] | undefined, key: string, value: boolean): CommerceProperty[] => {
    const existing = Array.isArray(props) ? props : [];
    let found = false;

    const next = existing.map(p => {
        if (p?.Key === key) {
            found = true;
            return { ...p, Value: { ...p.Value, BooleanValue: value } };
        }
        return p;
    });

    if (!found) {
        next.push({ Key: key, Value: { BooleanValue: value } });
    }

    return next;
};

export const getAttrText = (attrs: AttributeTextValue[] | undefined, name: string): string | undefined => {
    const a = attrs?.find(x => x?.Name === name) as AttributeTextValue | undefined;
    return a?.TextValue;
};

export const getSupersessionPreview = (line: CartLine | undefined): SupersessionPreview | undefined => {
    const text = getAttrText(line?.AttributeValues, SupersessionKey.SupersessionPreviewkey);
    if (!text) {
        return undefined;
    }

    const supersessionPreview = JSON.parse(text) as SupersessionPreview;

    if (!supersessionPreview) {
        return undefined;
    }

    return supersessionPreview;
};

export const hasSupersessionPreview = (line: CartLine | undefined): boolean => {
    const supersessionPreview = getSupersessionPreview(line);
    return supersessionPreview && supersessionPreview.Allocations.length > 0 ? true : false;
};
